import { useEffect } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import SmoothScroll from "@/components/site/SmoothScroll";
import { LanguageProvider } from "@/i18n";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FloatingTextCTA from "@/components/site/FloatingTextCTA";
import FloatingScanCTA from "@/components/site/FloatingScanCTA";
import MobileDock from "@/components/site/MobileDock";
import AnalyticsTracker from "@/components/site/AnalyticsTracker";

import Home from "@/pages/Home";
import BusinessWaste from "@/pages/BusinessWaste";
import DumpsterRentals from "@/pages/DumpsterRentals";
import RecyclingServices from "@/pages/RecyclingServices";
import WhoWeAre from "@/pages/WhoWeAre";
import QuickQuote from "@/pages/QuickQuote";
import Contact from "@/pages/Contact";
import DumpsterDetail from "@/pages/DumpsterDetail";
import Admin from "@/pages/Admin";
import CityLanding from "@/pages/CityLanding";
import ServiceAreas from "@/pages/ServiceAreas";
import SavingsCalculatorPage from "@/pages/SavingsCalculatorPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import { REDIRECTS } from "@/lib/data";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // Signal to the build-time prerenderer that this route has painted.
    const id = requestAnimationFrame(() => {
      document.body.setAttribute("data-prerender-ready", "true");
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);
  return null;
}

// Legacy Wix URL migration. Emergent SPA hosting can't emit a true server 301,
// so we combine an instant meta-refresh (Google treats a 0-second refresh like a
// permanent redirect) + a canonical to the new URL (consolidates ranking signals)
// + an instant client-side navigation for a seamless user experience.
function LegacyRedirect({ to }) {
  const dest = `https://eewaste.com${to}`;
  return (
    <>
      <meta httpEquiv="refresh" content={`0; url=${to}`} />
      <link rel="canonical" href={dest} />
      <Navigate to={to} replace />
    </>
  );
}

function App() {
  // Public routes are mirrored under `/es` for the Spanish site. `/admin` is intentionally excluded.
  const publicRoutes = [
    { path: "/", element: <Home /> },
    { path: "/business-waste-service", element: <BusinessWaste /> },
    { path: "/business-waste-service/:size", element: <DumpsterDetail variant="front" /> },
    { path: "/dumpster-rentals", element: <DumpsterRentals /> },
    { path: "/dumpster-rentals/:size", element: <DumpsterDetail variant="roll" /> },
    { path: "/recycling", element: <RecyclingServices /> },
    { path: "/blog", element: <Blog /> },
    { path: "/blog/:slug", element: <BlogPost /> },
    { path: "/who-we-are", element: <WhoWeAre /> },
    { path: "/quick-quote", element: <QuickQuote /> },
    { path: "/contact", element: <Contact /> },
    { path: "/dumpster-rental/:slug", element: <CityLanding /> },
    { path: "/service-areas", element: <ServiceAreas /> },
    { path: "/savings-calculator", element: <SavingsCalculatorPage /> },
  ];

  const esPath = (p) => (p === "/" ? "/es" : `/es${p}`);

  return (
    <SmoothScroll>
      <div className="App relative bg-[#FBFAF8] min-h-screen">
        <BrowserRouter>
          <LanguageProvider>
          <Toaster position="top-center" theme="light"
            toastOptions={{ style: { background: "#ffffff", border: "1px solid #E7E5DF", color: "#0B0F14" } }} />
          <ScrollToTop />
          <AnalyticsTracker />
          <Navbar />
          <main className="relative z-[1]">
            <Routes>
              {publicRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
              {publicRoutes.map((r) => (
                <Route key={esPath(r.path)} path={esPath(r.path)} element={r.element} />
              ))}
              <Route path="/admin" element={<Admin />} />
              {/* Legacy Wix URL redirects — meta-refresh + canonical + client nav to preserve SEO */}
              {REDIRECTS.map((r) => (
                <Route key={r.from} path={r.from} element={<LegacyRedirect to={r.to} />} />
              ))}
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <FloatingTextCTA />
          <FloatingScanCTA />
          <MobileDock />
          </LanguageProvider>
        </BrowserRouter>
      </div>
    </SmoothScroll>
  );
}

export default App;
